import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient } from '@/lib';
import type { GetPublishedChildExperienceInput } from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { createPublishedChildExperienceQueryKey } from '@/utils/queryKeys';
import { PageExperienceFragment } from '../fragments/PageExperience';
import type { PageExperience } from '../types';
import type {
  GetPublishedChildExperienceQuery,
  GetPublishedChildExperienceQueryVariables,
} from './useGetPublishedChildExperience.generated';

const query = gql`
  ${PageExperienceFragment}
  query GetPublishedChildExperience($input: GetPublishedChildExperienceInput!) {
    getPublishedChildExperience(input: $input) {
      ...PageExperience
    }
  }
`;

const selector = (data: GetPublishedChildExperienceQuery): PageExperience => {
  return data.getPublishedChildExperience;
};

// Anonymous-safe fetch for a planner child on published views. Children have
// no slugs of their own, so they are addressed by id through the parent
// planner's slug; the server only serves them while the parent is public.
export const useGetPublishedChildExperience = (
  parentExperienceSlug: string | undefined,
  childExperienceId: string | undefined
) => {
  const queryKey = createPublishedChildExperienceQueryKey(
    parentExperienceSlug,
    childExperienceId
  );

  return useQuery<GetPublishedChildExperienceQuery, QueryError, PageExperience>(
    {
      enabled: Boolean(parentExperienceSlug && childExperienceId),
      queryKey,
      select: selector,
      // Same rationale as useGetPageExperience: keep the previous child visible
      // while switching views so the loader doesn't flash through "loading".
      placeholderData: keepPreviousData,
      queryFn: () => {
        const input: GetPublishedChildExperienceInput = {
          // both protected by the `enabled` setting
          experienceSlug: parentExperienceSlug!,
          childExperienceId: childExperienceId!,
        };
        return gqlClient.request<
          GetPublishedChildExperienceQuery,
          GetPublishedChildExperienceQueryVariables
        >(query, { input });
      },
    }
  );
};
